/**
 * 변수 설정(ID/ID명/타입/Default/Format) 목록 조회 컨트롤.
 *
 * 겉모습은 SnCodeBox와 동일한 한 줄 입력칸 + 돋보기 버튼이다.
 * 돋보기를 누르면 등록된 변수를 표로 보여주는 팝업이 뜬다 — 보여주기만 하고 편집은 없다.
 * 입력칸에는 등록된 변수 ID를 콤마로 이어 보여준다.
 *
 * 값의 원천은 변수 속성(SnVarProps)이 쓰는 컬럼이다. 이 컨트롤은 값을 쓰지 않는다.
 */
import React from "react";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import VarSetListPopup, {
  type VarSet,
} from "@widgets/commonpopup/VarSetListPopup";

interface SnVarSetBoxProps {
  /** 보여줄 목록 */
  value: VarSet[];
  isReadOnly?: boolean;
  isRequired?: boolean;
  /** 행 미선택 등으로 입력 대상이 없을 때 (다른 Sn* 컨트롤과 동일하게 회색 처리) */
  isEditable?: boolean;
  inputRef?: any;
}

const SnVarSetBox = React.memo(
  ({
    value,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
  }: SnVarSetBoxProps) => {
    const rows = Array.isArray(value) ? value : [];
    // 한 줄이라 전부는 못 보여준다. ID만 이어 붙이고 나머지는 팝업에서 확인.
    const displayValue = rows
      .map((r) => r?.id)
      .filter(Boolean)
      .join(", ");

    const borderWidth = isRequired ? "2px" : "1px";
    const backgroundColor = isReadOnly || !isEditable ? "#F1F1F1" : "white";

    // 조회 전용이라 결과를 받지 않는다. 확인/취소 무엇으로 닫든 할 일이 없다.
    const openPopup = () => {
      showPopupModal({
        title: "변수 설정",
        children: <VarSetListPopup defaultValue={rows} />,
        // 그리드 도움창이라 높이를 고정한다 (auto면 그리드가 접힌다)
        width: "820px",
        height: "500px",
      });
    };

    return (
      <div className="relative w-full" style={{ padding: 0 }}>
        <TextField
          inputRef={inputRef}
          value={displayValue}
          placeholder="변수 속성에 등록하면 여기에 표시됩니다"
          fullWidth
          disabled={isReadOnly}
          slotProps={{
            input: {
              autoComplete: "off",
              // 조회 전용 — 타이핑도 지우기도 막는다
              readOnly: true,
              endAdornment: (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      openPopup();
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                    disabled={isReadOnly}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            },
          }}
          sx={{
            backgroundColor,
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderWidth: borderWidth },
              "&:hover fieldset": { borderWidth: borderWidth },
              "&.Mui-focused fieldset": { borderWidth: borderWidth },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
            },
            "& .MuiInputBase-input": { fontSize: StyleSettings.font },
          }}
        />
      </div>
    );
  }
);

export default SnVarSetBox;
